import { RESET_DB } from 'actions/orm'
import { GET_USER_EVENTS_SUCCESS, ResolvedAction } from 'actions/users'
import type { FeedbackRating } from 'components/FeedbackRatingIcon'
import { Model, ModelType } from 'redux-orm'
import { Fields as UserReward } from './UserReward'

type EventType =
  | 'reward_intro_delivered'
  | 'reward_ongoing_delivered'
  | 'reward_vip_delivered'
  | 'reward_used'
  | 'reward_purchased'
  | 'purchased'
  | 'transacted'
  | 'invitation_progress_granted'
  | 'merchant_progress_granted'
  | 'progress_provision_applied'
  | 'points_earned'
  | 'points_returned'
  | 'points_exchanged'
  | 'points_refunded'
  | 'points_expired'
  | 'points_expired_reverse'
  | 'order_rating_submitted'

type BaseFields = {
  id: number
  user_id: number
  occurred_at: string
  title: string
  location_id: number
  location_address: string
  related_id: number
}

interface RewardEvent extends BaseFields {
  type: Extract<
    EventType,
    | 'reward_intro_delivered'
    | 'reward_ongoing_delivered'
    | 'reward_vip_delivered'
    | 'reward_used'
  >
  related_item: UserReward
}

export interface TransactedEvent extends BaseFields {
  type: Extract<EventType, 'transacted'>
  related_item: { granted: boolean }
}

export interface RewardPurchasedEvent extends BaseFields {
  type: Extract<EventType, 'reward_purchased'>
  related_item: { price: string }
}

// AI-1289: order_rating_submitted event, added to the customer activity
// stream when a customer responds to an order rating prompt (thumbs up or
// down). `order_rating_id` is the OrderRating HASHID (a raw integer id 404s
// on the detail route) — see utilities/orderRatingViewPath. `rating` carries
// the polarity, so the stream's thumbs icon points the right way; the
// server-built `title` reads the same for both directions and is the only
// rendered copy, including the "· left a comment" variant.
// `conversation_id` is null for a rating with no RecoveryAI conversation, and
// picks the destination of the View feedback link exactly as it does on the
// Orders tab and the Order feedback tab — all three go through
// utilities/orderRatingViewPath.
export interface OrderRatingSubmittedEvent extends BaseFields {
  type: Extract<EventType, 'order_rating_submitted'>
  related_item: {
    order_rating_id: string
    rating: FeedbackRating
    conversation_id: string | null
  }
}

interface StandardEvent extends BaseFields {
  type: Exclude<
    EventType,
    | 'reward_intro_delivered'
    | 'reward_ongoing_delivered'
    | 'reward_vip_delivered'
    | 'reward_used'
    | 'reward_purchased'
    | 'transacted'
    | 'order_rating_submitted'
  >
  related_item: null
}

export type Fields =
  | RewardEvent
  | TransactedEvent
  | RewardPurchasedEvent
  | OrderRatingSubmittedEvent
  | StandardEvent

// @ts-ignore
export default class UserEvent extends Model<typeof UserEvent, Fields> {
  static modelName: string = 'UserEvent'

  static reducer(
    action: ResolvedAction | { type: typeof RESET_DB },
    // @ts-ignore
    UserEvent: ModelType<UserEvent>
  ): void {
    switch (action.type) {
      case GET_USER_EVENTS_SUCCESS:
        action.payload.data.events.forEach(event => UserEvent.upsert(event))
        break
      case RESET_DB:
        UserEvent.all().delete()
        break
      default:
        break
    }
  }
}
